import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { LanguageSelector } from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Zap, Droplets, ChevronLeft, Trash2, AlertTriangle, Mail, Loader2, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DeleteAccountPage = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    email: '',
    reason: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/account/deletion-request`, form);
      toast.success(response.data.message);
      setSubmitted(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'Error submitting request';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-primary">
                <Zap className="h-5 w-5" strokeWidth={2.5} />
                <Droplets className="h-5 w-5 text-blue-500" strokeWidth={2.5} />
              </div>
              <span className="font-bold text-xl tracking-tight">Safi Energy</span>
            </Link>
            <LanguageSelector variant="ghost" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
          <ChevronLeft className="h-4 w-4" />
          {t('nav_application')}
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <Trash2 className="h-8 w-8 text-destructive" />
          <h1 className="text-4xl font-bold tracking-tight uppercase">{t('footer_delete')}</h1>
        </div>

        {submitted ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center space-y-6">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold">Request Submitted Successfully</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Your account deletion request has been recorded. It will be processed within 30 days.
              You will receive a confirmation email once your data has been deleted.
            </p>
            <Link to="/">
              <Button className="mt-4">
                Return to Home
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <p className="text-muted-foreground mb-8">
              Use this form to request the deletion of your SafiEco account and all associated personal data.
              Your request will be processed within 30 days in accordance with our <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
            </p>

            <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-6 w-6 text-destructive flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-destructive text-lg">Warning: This action is irreversible</p>
                  <p className="text-muted-foreground mt-2">
                    Once your request is processed, all your data will be permanently deleted including:
                  </p>
                  <ul className="list-disc pl-6 mt-3 text-muted-foreground space-y-2">
                    <li>Your account and profile information</li>
                    <li>All outage reports you have submitted</li>
                    <li>Your confirmation history and earned points</li>
                    <li>Any solar quote requests associated with your email</li>
                  </ul>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-8 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="deletion-email" className="text-sm font-medium uppercase tracking-wide">
                  Email Address <span className="text-destructive">*</span>
                </Label>
                <p className="text-sm text-muted-foreground">
                  Enter the email address associated with your SafiEco account
                </p>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="deletion-email"
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="pl-12 h-14 bg-input/50 border-border focus:border-destructive text-lg"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deletion-reason" className="text-sm font-medium uppercase tracking-wide">
                  Reason for Leaving (Optional)
                </Label>
                <p className="text-sm text-muted-foreground">
                  Help us improve SafiEco by sharing why you want to delete your account
                </p>
                <Textarea
                  id="deletion-reason"
                  placeholder="Tell us why you're leaving (optional)..."
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  className="min-h-[120px] bg-input/50 border-border focus:border-destructive resize-none"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/" className="flex-1">
                  <Button type="button" variant="outline" className="w-full h-12">
                    Cancel
                  </Button>
                </Link>
                <Button
                  type="submit"
                  className="flex-1 h-12 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={loading || !form.email}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="h-5 w-5 mr-2" />
                      Submit Deletion Request
                    </>
                  )}
                </Button>
              </div>
            </form>

            <div className="mt-8 text-center text-sm text-muted-foreground">
              <p>
                Questions? Contact us at <a href="mailto:info@safienergy.org" className="text-primary hover:underline">info@safienergy.org</a>
              </p>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-border bg-card mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">{t('footer_copyright')}</p>
        </div>
      </footer>
    </div>
  );
};
